import type { BusinessReport } from "../businessReports.types";
import type { SnapshotCollection, SnapshotCollectionStatus } from "../snapshotCollection.types";
import { getTemplateDisplayCategory } from "../templateLibrary";

export type SnapshotCollectionsDashboardSegment = "all" | "pinned" | "shared" | "inProgress";

export interface SnapshotCollectionsSummary {
  collections: number;
  needsReview: number;
  snapshots: number;
  processing: number;
}

export interface SnapshotTemplateGroup {
  template: BusinessReport;
  collections: SnapshotCollection[];
  snapshots: number;
  latestCreatedAt: string | null;
  isPinned: boolean;
}

export interface SnapshotTemplateCategorySection {
  label: string;
  groups: SnapshotTemplateGroup[];
}

export interface SnapshotTemplateNavigatorModel {
  primaryLabel: string;
  secondaryLabel: string;
  primaryGroups: SnapshotTemplateGroup[];
  secondaryGroups: SnapshotTemplateGroup[];
  categorySections: SnapshotTemplateCategorySection[];
  emptyTemplates: BusinessReport[];
}

export interface SnapshotCollectionTableRow {
  collection: SnapshotCollection;
  template: BusinessReport | null;
  templateName: string;
}

export interface SnapshotCollectionStatusMeta {
  label: string;
  dotClassName: string;
  textClassName: string;
}

interface BuildTemplateNavigatorModelParams {
  templates: BusinessReport[];
  collections: SnapshotCollection[];
  allCollections?: SnapshotCollection[];
  emptyTemplateQuery?: string;
  favoriteTemplateIds: ReadonlySet<string>;
  segment: SnapshotCollectionsDashboardSegment;
}

interface ResolveSnapshotCollectionsRouteFocusParams {
  businessReportId: string | null;
  snapshotCollectionId: string | null;
  collections: SnapshotCollection[];
  templates: BusinessReport[];
}

export interface SnapshotCollectionsRouteFocus {
  key: string;
  query: string;
}

const PROCESSING_STATUSES = new Set<SnapshotCollectionStatus>(["IN_PROGRESS", "NEW"]);

const getCreatedTime = (createdAt?: string | null): number => {
  if (!createdAt) return 0;

  const time = Date.parse(createdAt);
  if (Number.isNaN(time)) return 0;

  return time;
};

const sortByTemplateName = <T extends { template: BusinessReport }>(groups: T[]): T[] =>
  [...groups].sort((first, second) => first.template.name.localeCompare(second.template.name));

const sortTemplatesByName = (templates: BusinessReport[]): BusinessReport[] =>
  [...templates].sort((first, second) => first.name.localeCompare(second.name));

const sortGroupsByLatestCreatedAt = (groups: SnapshotTemplateGroup[]): SnapshotTemplateGroup[] =>
  [...groups].sort((first, second) => {
    const createdAtDifference = getCreatedTime(second.latestCreatedAt) - getCreatedTime(first.latestCreatedAt);
    if (createdAtDifference !== 0) return createdAtDifference;

    return first.template.name.localeCompare(second.template.name);
  });

const buildTemplateCategorySections = (groups: SnapshotTemplateGroup[]): SnapshotTemplateCategorySection[] => {
  const groupsByCategory = new Map<string, SnapshotTemplateGroup[]>();

  for (const group of groups) {
    const label = getTemplateDisplayCategory(group.template);
    const categoryGroups = groupsByCategory.get(label) ?? [];
    categoryGroups.push(group);
    groupsByCategory.set(label, categoryGroups);
  }

  return Array.from(groupsByCategory, ([label, categoryGroups]) => ({
    label,
    groups: sortByTemplateName(categoryGroups),
  })).sort((first, second) => {
    if (first.label === "Uncategorised") return 1;
    if (second.label === "Uncategorised") return -1;
    return first.label.localeCompare(second.label);
  });
};

const hasSharedCollections = (collections: SnapshotCollection[]): boolean =>
  collections.some(collection => collection.access === "public" || collection.permissions.length > 1);

const hasProcessingCollections = (collections: SnapshotCollection[]): boolean =>
  collections.some(collection => PROCESSING_STATUSES.has(collection.status));

const buildFallbackTemplate = (businessReportId: string): BusinessReport =>
  ({
    id: businessReportId,
    name: "Unknown template",
    is_active: true,
    type: "snapshot_template",
  }) as BusinessReport;

const getLatestCreatedAt = (collections: SnapshotCollection[]): string | null => {
  let latestCollection: SnapshotCollection | null = null;

  for (const collection of collections) {
    if (getCreatedTime(collection.created_at) > getCreatedTime(latestCollection?.created_at)) {
      latestCollection = collection;
    }
  }

  return latestCollection?.created_at ?? null;
};

export const getDashboardSummary = (collections: SnapshotCollection[]): SnapshotCollectionsSummary => ({
  collections: collections.length,
  needsReview: collections.filter(collection => collection.status === "FAILED").length,
  snapshots: collections.reduce((total, collection) => total + collection.snapshots, 0),
  processing: collections.filter(collection => PROCESSING_STATUSES.has(collection.status)).length,
});

export const buildTemplateNavigatorModel = ({
  templates,
  collections,
  allCollections = collections,
  emptyTemplateQuery = "",
  favoriteTemplateIds,
  segment,
}: BuildTemplateNavigatorModelParams): SnapshotTemplateNavigatorModel => {
  const activeTemplates = templates.filter(template => template.is_active !== false);
  const collectionsByTemplateId = new Map<string, SnapshotCollection[]>();
  const allCollectionTemplateIds = new Set(allCollections.map(collection => collection.business_report_id));
  const loadedTemplateIds = new Set(templates.map(template => template.id));

  for (const collection of collections) {
    const templateCollections = collectionsByTemplateId.get(collection.business_report_id) ?? [];
    templateCollections.push(collection);
    collectionsByTemplateId.set(collection.business_report_id, templateCollections);
  }

  // Hidden templates (Hide action -> is_active false) must not surface in the navigator,
  // so their category sections disappear with them. loadedTemplateIds still covers hidden
  // templates, which keeps their collections out of the "Unknown template" fallback below.
  const templateGroups = activeTemplates.flatMap(template => {
    const templateCollections = collectionsByTemplateId.get(template.id) ?? [];
    if (templateCollections.length === 0) return [];

    return [
      {
        template,
        collections: templateCollections,
        snapshots: templateCollections.reduce((total, collection) => total + collection.snapshots, 0),
        latestCreatedAt: getLatestCreatedAt(templateCollections),
        isPinned: favoriteTemplateIds.has(template.id),
      },
    ];
  });
  const missingTemplateGroups = Array.from(collectionsByTemplateId.entries()).flatMap(
    ([businessReportId, templateCollections]) => {
      if (loadedTemplateIds.has(businessReportId)) return [];

      return [
        {
          template: buildFallbackTemplate(businessReportId),
          collections: templateCollections,
          snapshots: templateCollections.reduce((total, collection) => total + collection.snapshots, 0),
          latestCreatedAt: getLatestCreatedAt(templateCollections),
          isPinned: favoriteTemplateIds.has(businessReportId),
        },
      ];
    },
  );
  const groups = [...templateGroups, ...missingTemplateGroups];

  const normalizedEmptyTemplateQuery = emptyTemplateQuery.trim().toLowerCase();
  const emptyTemplates = sortTemplatesByName(
    activeTemplates.filter(template => {
      if (allCollectionTemplateIds.has(template.id)) return false;
      if (!normalizedEmptyTemplateQuery) return true;

      return template.name.toLowerCase().includes(normalizedEmptyTemplateQuery);
    }),
  );

  if (segment === "pinned") {
    const secondaryGroups = sortByTemplateName(groups.filter(group => !group.isPinned));
    return {
      primaryLabel: "Pinned templates",
      secondaryLabel: "Other templates",
      primaryGroups: sortGroupsByLatestCreatedAt(groups.filter(group => group.isPinned)),
      secondaryGroups,
      categorySections: buildTemplateCategorySections(secondaryGroups),
      emptyTemplates,
    };
  }

  if (segment === "shared") {
    return {
      primaryLabel: "Shared templates",
      secondaryLabel: "Other templates",
      primaryGroups: sortGroupsByLatestCreatedAt(groups.filter(group => hasSharedCollections(group.collections))),
      secondaryGroups: [],
      categorySections: [],
      emptyTemplates,
    };
  }

  if (segment === "inProgress") {
    return {
      primaryLabel: "In progress templates",
      secondaryLabel: "Other templates",
      primaryGroups: sortGroupsByLatestCreatedAt(groups.filter(group => hasProcessingCollections(group.collections))),
      secondaryGroups: [],
      categorySections: [],
      emptyTemplates,
    };
  }

  return {
    primaryLabel: "Recent templates",
    secondaryLabel: "Other templates",
    primaryGroups: sortGroupsByLatestCreatedAt(groups),
    secondaryGroups: [],
    categorySections: [],
    emptyTemplates,
  };
};

export const buildCollectionRows = (
  collections: SnapshotCollection[],
  templates: BusinessReport[],
): SnapshotCollectionTableRow[] => {
  const templatesById = new Map(templates.map(template => [template.id, template]));

  return collections.map(collection => {
    const template = templatesById.get(collection.business_report_id) ?? null;

    return {
      collection,
      template,
      templateName: template?.name ?? "Unknown template",
    };
  });
};

export const filterCollectionRows = (
  rows: SnapshotCollectionTableRow[],
  query: string,
): SnapshotCollectionTableRow[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return rows;

  return rows.filter(row => {
    const collectionName = row.collection.name.toLowerCase();
    const templateName = row.templateName.toLowerCase();

    return collectionName.includes(normalizedQuery) || templateName.includes(normalizedQuery);
  });
};

export const filterSidebarCollections = (
  collections: SnapshotCollection[],
  templates: BusinessReport[],
  query: string,
): SnapshotCollection[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return collections;

  const templateById = new Map(templates.map(template => [template.id, template]));

  return collections.filter(collection => {
    const template = templateById.get(collection.business_report_id);
    const templateName = template?.name.toLowerCase() ?? "unknown template";
    const categoryName = template ? getTemplateDisplayCategory(template).toLowerCase() : "uncategorised";

    return (
      collection.name.toLowerCase().includes(normalizedQuery) ||
      templateName.includes(normalizedQuery) ||
      categoryName.includes(normalizedQuery)
    );
  });
};

export const resolveSnapshotCollectionsRouteFocus = ({
  businessReportId,
  snapshotCollectionId,
  collections,
  templates,
}: ResolveSnapshotCollectionsRouteFocusParams): SnapshotCollectionsRouteFocus | null => {
  if (snapshotCollectionId) {
    const collection = collections.find(row => row.id === snapshotCollectionId);
    if (collection) {
      return {
        key: `collection:${collection.id}`,
        query: collection.name,
      };
    }
  }

  if (businessReportId) {
    const template = templates.find(row => row.id === businessReportId);
    if (template) {
      return {
        key: `template:${template.id}`,
        query: template.name,
      };
    }
  }

  return null;
};

export const getCollectionStatusMeta = (status: SnapshotCollectionStatus): SnapshotCollectionStatusMeta => {
  const statusMeta: Record<SnapshotCollectionStatus, SnapshotCollectionStatusMeta> = {
    COMPLETED: {
      label: "Completed",
      dotClassName: "bg-emerald-600",
      textClassName: "text-gray-700",
    },
    IN_PROGRESS: {
      label: "Processing",
      dotClassName: "bg-blue-600",
      textClassName: "text-gray-700",
    },
    NEW: {
      label: "Processing",
      dotClassName: "bg-blue-600",
      textClassName: "text-gray-700",
    },
    FAILED: {
      label: "Failed",
      dotClassName: "bg-red-600",
      textClassName: "text-gray-700",
    },
    CANCELLED: {
      label: "Cancelled",
      dotClassName: "bg-gray-500",
      textClassName: "text-gray-700",
    },
  };

  return statusMeta[status];
};
